import { useCallback, useEffect, useState } from "react";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";
import useUser from "../context/userHook";
import useBoardAndItems from "../context/useBoardAndItems";
import useTokenHook from "../context/useTokenHook";
import { getBoardsList } from "../../utils/requests/boardsRequest";
import type { BoardType } from "../../components/addItem/BoardsList";

const SWR_KEY_PREFIX = "workspace-boards-";

export type BoardsByWorkspaceMap = Record<string, BoardType[]>;

export function useWorkspaceBoards() {
  const navigate = useNavigate();
  const { userId } = useUser();
  const { currentBoard, itemIds } = useBoardAndItems();
  const { token } = useTokenHook();

  const [boardsByWorkspace, setBoardsByWorkspace] = useState<BoardsByWorkspaceMap>({});
  const [requestedWorkspaceId, setRequestedWorkspaceId] = useState<string | null>(null);

  const swrKey =
    token && userId && requestedWorkspaceId
      ? `${SWR_KEY_PREFIX}${requestedWorkspaceId}:${userId}`
      : null;

  const fetcher = useCallback(async (): Promise<BoardType[]> => {
    if (!requestedWorkspaceId || !token) return [];
    const result = await getBoardsList(token, itemIds, currentBoard, Number(requestedWorkspaceId));
    return Array.isArray(result) ? result : [];
  }, [requestedWorkspaceId, token, itemIds, currentBoard]);

  const { data, isLoading, error, mutate } = useSWR<BoardType[]>(swrKey, fetcher, {
    revalidateOnFocus: false,
    onError: (err: { response?: { status?: number; data?: { message?: string } } }) => {
      if (err?.response?.status === 401 && err?.response?.data?.message === "User not found") {
        navigate("/onboarding");
      }
    },
  });

  useEffect(() => {
    if (data && requestedWorkspaceId) {
      setBoardsByWorkspace((prev) => ({ ...prev, [requestedWorkspaceId]: data }));
    }
  }, [data, requestedWorkspaceId]);

  const fetchBoardsForWorkspace = useCallback((workspaceId: string) => {
    setRequestedWorkspaceId(workspaceId);
  }, []);

  const loadingWorkspaceId = isLoading && requestedWorkspaceId ? requestedWorkspaceId : null;

  return {
    boardsByWorkspace,
    fetchBoardsForWorkspace,
    loadingWorkspaceId,
    isLoadingBoards: isLoading,
    error,
    mutateWorkspaceBoards: mutate,
  };
}
